/**
 * Sertifika Doğrulama — Spec Bölüm 8.6 + S3.5
 *
 *   /sertifika/:certificateId
 *
 * Public sayfa — HR / işveren burayı LinkedIn linkinden açar.
 * Geçerlilik, skor seviyesi, hedef SW sürümü gösterilir.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'wouter';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';

interface CertificateData {
  certificateId: string;
  validUntil: string;
  isExpired: boolean;
  isAnonymous: boolean;
  displayName: string;
  organization: string | null;
  score: number | null;
  scoreLevel: string | null;
  swVersionTarget: string | null;
  examBankVersion: string | null;
  completedAt: string | null;
  questionCount: number;
  durationMin: number;
}

const LEVEL_LABELS: Record<string, string> = {
  L0: 'Tanışma',
  L1_MINUS: 'Başlangıç',
  L1: 'CSWA Seviyesi',
  L2: 'CSWA Üzeri',
  L3: 'CSWP Seviyesi',
  L4: 'İleri Seviye',
};

const LEVEL_DESC: Record<string, string> = {
  L2: 'CSWA seviyesini geçerek 1-3 yıl deneyimli mühendis profilini sergilemiştir.',
  L3: 'CSWP seviyesinde olup 3+ yıl deneyimli, ileri SolidWorks kullanıcısıdır.',
  L4: 'CSWE\'ye yakın seviyede, ekip lideri ve domain uzmanı profilindedir.',
};

export function CertificateVerifyPage(): JSX.Element {
  const params = useParams<{ certificateId: string }>();
  const certificateId = (params.certificateId ?? '').toUpperCase();
  const [data, setData] = useState<CertificateData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!certificateId) {
      setError('Geçersiz sertifika kimliği');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<CertificateData>(
          `/api/public/verify/${certificateId}`,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Sertifika doğrulanamadı');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [certificateId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link href="/" className="text-2xl font-bold tracking-tight">
            <span className="text-blue-900">BMU</span>
            <span className="text-sky-500">Works</span>
            <span className="ml-2 text-sm font-medium text-slate-500">
              SkillProbe
            </span>
          </Link>
          <span className="text-xs uppercase tracking-wider text-slate-500">
            Sertifika Doğrulama
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        {error && (
          <Card>
            <Alert variant="danger">{error}</Alert>
            <p className="mt-4 text-sm text-slate-600">
              Sertifika numarasını kontrol edip tekrar deneyin.
              Aramayı 12 haneli kimlik ile yapın (örn. <code>5K8M3J0PQ7H2</code>).
            </p>
          </Card>
        )}

        {!error && !data && (
          <Card>
            <p className="text-center text-slate-500">
              Sertifika doğrulanıyor…
            </p>
          </Card>
        )}

        {data && <CertificateCard data={data} />}
      </main>
    </div>
  );
}

function CertificateCard({ data }: { data: CertificateData }): JSX.Element {
  const validUntil = new Date(data.validUntil);
  const completedAt = data.completedAt ? new Date(data.completedAt) : null;
  const levelLabel = data.scoreLevel ? LEVEL_LABELS[data.scoreLevel] ?? data.scoreLevel : '—';
  const levelDesc = data.scoreLevel ? LEVEL_DESC[data.scoreLevel] : null;

  return (
    <>
      {data.isExpired && (
        <div className="mb-4">
          <Alert variant="warning">
            <strong>Bu sertifikanın süresi {validUntil.toLocaleDateString('tr-TR')} tarihinde dolmuştur.</strong>{' '}
            Aday güncel yetkinliğini yeniden değerlendirmeli.
          </Alert>
        </div>
      )}

      <div className="rounded-2xl border-2 border-blue-900 bg-white p-8 shadow-lg sm:p-10">
        {/* Geçerli rozet */}
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500">
            BMU SkillProbe Sertifikası
          </div>
          {!data.isExpired ? (
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">
              ✓ Geçerli
            </span>
          ) : (
            <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
              Süresi Dolmuş
            </span>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm font-medium text-slate-600">Bu belge,</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900 sm:text-4xl">
            {data.displayName}
          </h1>
          {data.organization && (
            <p className="mt-1 text-base text-slate-700">
              ({data.organization})
            </p>
          )}
          <p className="mt-6 text-sm text-slate-600">
            adının, BMU SkillProbe değerlendirmesinde
          </p>
          <p className="mt-3 text-2xl font-semibold text-blue-900">
            {data.scoreLevel} · {levelLabel}
          </p>
          <p className="mt-1 font-mono text-sm text-slate-500">
            ({data.score}/100)
          </p>
          {levelDesc && (
            <p className="mx-auto mt-4 max-w-md text-sm text-slate-700">
              {levelDesc}
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-4 border-t border-slate-200 pt-6 sm:grid-cols-2">
          <Detail label="Sertifika ID" value={data.certificateId} mono />
          <Detail
            label="Geçerlilik"
            value={validUntil.toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          />
          <Detail
            label="Sınav Tarihi"
            value={
              completedAt
                ? completedAt.toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '—'
            }
          />
          <Detail
            label="Hedef SW Sürümü"
            value={data.swVersionTarget ?? '—'}
          />
          <Detail
            label="Soru Sayısı"
            value={`${data.questionCount} soru`}
          />
          <Detail label="Süre" value={`${data.durationMin} dakika`} />
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6 text-center">
          <a
            href={`/api/public/verify/${data.certificateId}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="mb-4 inline-block rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white hover:bg-blue-800"
          >
            📄 PDF olarak indir
          </a>
          <p className="text-xs text-slate-500">
            Bu sertifika BMU Works tarafından kişisel olarak değil,
            BMU SkillProbe değerlendirme platformu tarafından otomatik
            üretilmiştir. Doğrulama URL'i:
          </p>
          <p className="mt-1 font-mono text-xs text-slate-700">
            swskills.bmuworks.com/sertifika/{data.certificateId}
          </p>
        </div>
      </div>

      <div className="mt-6 text-center text-sm text-slate-600">
        Ekibiniz için yetkinlik değerlendirmesi yaptırmak ister misiniz?{' '}
        <Link
          href="/yonetici/kayit"
          className="font-medium text-blue-900 hover:underline"
        >
          Ücretsiz başlayın
        </Link>
      </div>
    </>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p
        className={`mt-1 text-sm font-medium text-slate-900 ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value}
      </p>
    </div>
  );
}
